import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Dialog } from "../../components/ui/Dialog";
import { Field, TextInput } from "../../components/ui/Field";
import { EmptyState, PageTitle, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { ApiTokenRow } from "../../lib/api/models";
import { formatDate, formatDateTime } from "../../lib/format";
import { qk } from "../../lib/queries";

const status = (t: ApiTokenRow) =>
  t.revokedAt ? "Revoked" : new Date(t.expiresAt).getTime() < Date.now() ? "Expired" : "Active";

export function TokensAdmin() {
  const queryClient = useQueryClient();
  const { data: tokens, isLoading } = useQuery({ queryKey: qk.tokens, queryFn: api.tokens.list });
  const { confirm, element } = useConfirm();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Array<"read" | "write">>(["read"]);
  const [days, setDays] = useState("90");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.tokens });

  const toggle = (s: "read" | "write") =>
    setScopes((list) => (list.includes(s) ? list.filter((x) => x !== s) : [...list, s]));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = Number(days);
    if (!name.trim()) return setError("Enter a name, for example claude-ops.");
    if (scopes.length === 0) return setError("Choose at least one scope.");
    if (!Number.isInteger(n) || n < 1 || n > 365) return setError("Choose between 1 and 365 days.");
    setError(null);
    try {
      const r = await api.tokens.create({ name: name.trim(), scopes, expiresInDays: n });
      setCreated(r.token);
      setName("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The token could not be created.");
    }
  };

  return (
    <div>
      <PageTitle>API tokens</PageTitle>
      <p className="mb-6 max-w-2xl text-ink-2">
        Tokens let Claude and scripts use <code className="font-mono text-xs">/api/v1</code> with your permissions. Use{" "}
        <code className="font-mono text-xs">Authorization: Bearer kd_…</code>. A token is shown once.
      </p>
      <form
        onSubmit={submit}
        noValidate
        className="mb-8 grid gap-3 rounded-xl border border-line bg-surface p-5 sm:grid-cols-[1.4fr_auto_0.8fr_auto] sm:items-end"
      >
        <Field label="Name" error={error}>
          {(id, describedBy) => (
            <TextInput id={id} aria-describedby={describedBy} value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
        <fieldset className="flex h-9 items-center gap-3 text-sm">
          <legend className="sr-only">Scopes</legend>
          {(["read", "write"] as const).map((s) => (
            <label key={s} className="flex items-center gap-1.5">
              <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggle(s)} />
              {s}
            </label>
          ))}
          <span className="text-xs text-ink-3">(write includes read)</span>
        </fieldset>
        <Field label="Expires in (days)">
          {(id) => (
            <TextInput id={id} type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
          )}
        </Field>
        <Button type="submit" variant="primary">
          Create token
        </Button>
      </form>
      {isLoading ? <Spinner label="Loading tokens…" /> : null}
      {tokens && tokens.length === 0 ? <EmptyState title="No API tokens yet" /> : null}
      {tokens && tokens.length > 0 ? (
        <Panel>
          <div className="overflow-x-auto">
            <table className="kd-datatable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Owner</th>
                  <th>Scopes</th>
                  <th>Expires</th>
                  <th>Last used</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id}>
                    <td className="font-mono text-xs">{t.name}</td>
                    <td className="text-ink-2">{t.user.email}</td>
                    <td>{t.scopes.join(", ")}</td>
                    <td className="text-ink-3">{formatDate(t.expiresAt)}</td>
                    <td className="text-ink-3">{t.lastUsedAt ? formatDateTime(t.lastUsedAt) : "Never"}</td>
                    <td>{status(t)}</td>
                    <td className="text-right">
                      {t.revokedAt ? null : (
                        <Button
                          size="sm"
                          variant="danger"
                          aria-label={`Revoke ${t.name}`}
                          onClick={async () => {
                            if (
                              await confirm({
                                title: `Revoke ${t.name}?`,
                                body: "Anything using this token stops working at once.",
                                confirmLabel: "Revoke",
                                danger: true,
                              })
                            ) {
                              try {
                                await api.tokens.revoke(t.id);
                                toast(`${t.name} revoked.`, "success");
                              } catch (e) {
                                toast(`Revoke failed: ${e instanceof Error ? e.message : "unknown error"}`, "error");
                              }
                              await refresh();
                            }
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}
      <Dialog
        open={created !== null}
        onClose={() => setCreated(null)}
        dismissible={false}
        title="Copy the new token now"
        description="It is stored only as a hash and is not shown again."
        footer={
          <>
            <Button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(created ?? "");
                  toast("Token copied.", "success");
                } catch {
                  toast("Copy failed; select the token and copy it by hand.", "warning");
                }
              }}
            >
              Copy
            </Button>
            <Button variant="primary" onClick={() => setCreated(null)}>
              I have copied it
            </Button>
          </>
        }
      >
        <code className="block rounded-md bg-column px-3 py-2 font-mono text-sm break-all select-all">{created}</code>
      </Dialog>
      {element}
    </div>
  );
}
